<!-- Copyright (c) 2025 Digital Systems (M) Sdn. Bhd. -->

<!--
  Usage:
  <FiltersPopup
    :value.sync="filters"
    :filterDefinition="filterDefinition"
    @save="save"
    @cancel="cancel"
  />

  Description:
  Filter popup component used to let users add, remove, join, validate, and save filters.
  Supports multiple filter input types based on filterDefinition.
  If filterDefinition is empty, component will use the default demo filter types.

  Props:
  value: {
    type: Array,
    default: () => [],
  },
  // Bind this value with .sync to receive saved filters.
  // Each filter item should match the saved value output format below.

  filterDefinition: {
    type: Array,
    default: () => [],
  },
    // Defines the available filters shown in the Filter dropdown.
    // If not provided, default FILTER_TYPES will be used.

  filterDefinition: [
    {
      label: String,              // shown in dropdown / placeholder
      key: String,                // unique filter key saved in output
      filterType: 'string_value', // single text input
      validator: String,          // optional, passed to Input validator

      // Output:
      // { key, label, filterType: 'string_value', joinType, value }
    },

    {
      label: String,
      key: String,
      filterType: 'string_range', // from / to text input
      validator: String,          // optional, passed to Input validator

      // Output:
      // { key, label, filterType: 'string_range', joinType, fromStr, toStr }
    },

    {
      label: String,
      key: String,
      filterType: 'date', // single date-only picker

      // Output:
      // { key, label, filterType: 'date', joinType, value }
    },

    {
      label: String,
      key: String,
      filterType: 'date_value', // single date picker

      // Output:
      // { key, label, filterType: 'date_value', joinType, value }
    },

    {
      label: String,
      key: String,
      filterType: 'date_range', // from / to date picker

      // Output:
      // { key, label, filterType: 'date_range', joinType, from, to }
    },

    {
      label: String,
      key: String,
      filterType: 'dropdown_list', // single dropdown selection
      options: Array,              // required if optionsFunction is not used
      optionsFunction: Function,   // optional async function returning options array
      saveKey: String,             // optional, defaults to 'key'

      // Option format:
      // [{ label: 'Display Text', key: 'SavedValue' }]

      // Custom option format with saveKey:
      // [{ label: 'ABC Customer', 'Customer Code': 'C001' }]

      // Output:
      // { key, label, filterType: 'dropdown_list', joinType, value }
    },

    {
      label: String,
      key: String,
      filterType: 'multiselect',   // multiple dropdown selections
      options: Array,              // required if optionsFunction is not used
      optionsFunction: Function,   // optional async function returning options array
      saveKey: String,             // optional, defaults to 'key'

      // Option format:
      // [{ label: 'Display Text', key: 'SavedValue' }]

      // Custom option format with saveKey:
      // [{ label: 'ABC Customer', 'Customer Code': 'C001' }]

      // Output:
      // { key, label, filterType: 'multiselect', joinType, selected: [{ label, key }] }
    },

    {
      label: String,
      key: String,
      filterType: 'boolean', // yes / no selection

      // Output:
      // { key, label, filterType: 'boolean', joinType, value }
    },

    {
      label: String,
      key: String,
      filterType: 'null_check', // any / has value / is empty

      // Output:
      // { key, label, filterType: 'null_check', joinType, value }
    },
  ]
  Public events:
  @update:value
  - Fired when Save is clicked and validation passes.
  - Returns saved filters without internal uid.

  @save
  - Fired after update:value when validation passes.

  @cancel
  - Fired when Cancel is clicked.
  - Restores filterItems to original value snapshot and does not update parent value.

  Validation:
  - string_value: value is required
  - string_range: fromStr and toStr are required
  - date: value is required
  - date_value: value is required
  - date_range: from is required
  - dropdown_list: value is required
  - multiselect: at least one selected value is required
  - boolean: true or false must be selected
  - empty filter type: filter type must be selected

  Notes:
  - Each filterDefinition key can only be selected once.
  - JOIN type defaults to AND.
  - OR / AND join is only shown from the second filter onward.
  - Clear All asks for confirmation before removing all filters.
  - optionsFunction result is cached in loadedOptions by filter key.
  - dropdown_list saves its selected value using saveKey, or 'key' by default.
  - multiselect saves complete selected option objects; consumers normalize saveKey values only when building API payloads.
-->

<template>
  <div class="filter-popup-wrapper">
    <div class="flex-container title-wrapper">
      <div style="font-weight: bold">Filters</div>
      <Button @click="addFilter" class="ml-auto" :disabled="!canAddFilter">Add Filter</Button>
    </div>
    <div class="filter-list">
      <div v-for="(filterItem, index) in filterItems" :key="filterItem.uid" class="mb-2">
        <div class="flex-container ml-2 mb-2" v-if="index > 0 && filterItems.length > 1">
          JOIN
          <div class="join-toggle">
            <span
              class="join-btn"
              :class="{ active: filterItem.joinType == 'AND' }"
              @click="filterJoinClick(filterItem.uid, 'AND')"
              >AND</span
            >
            <span
              class="join-btn"
              :class="{ active: filterItem.joinType == 'OR' }"
              @click="filterJoinClick(filterItem.uid, 'OR')"
              >OR</span
            >
          </div>
        </div>
        <div class="filter-item">
          <div class="flex-container">
            <Dropdown
              :items="getAvailableTypes(filterItem.uid)"
              :selectedObject="getSelectedType(filterItem)"
              itemKey="label"
              @selected="(e) => filterTypeSelected(filterItem.uid, e)"
              placeholder="Filter"
            />
            <Button @click="filterItemDelete(filterItem.uid)">Delete</Button>
          </div>

          <Input
            v-if="['string_value', 'number_value'].includes(getSelectedTypeString(filterItem))"
            :inputValue.sync="filterItem.value"
            :placeholder="filterItem.label"
            :validator="getValidatorString(filterItem.key)"
          />

          <div class="flex-container" v-else-if="getSelectedTypeString(filterItem) == 'string_range'">
            <Input
              :inputValue.sync="filterItem.fromStr"
              placeholder="From"
              :validator="getValidatorString(filterItem.key)"
            />
            to
            <Input
              :inputValue.sync="filterItem.toStr"
              placeholder="To"
              :validator="getValidatorString(filterItem.key)"
            />
          </div>

          <DateTimePicker
            v-else-if="getSelectedTypeString(filterItem) == 'date'"
            :value="filterItem.value ? new Date(filterItem.value) : null"
            :showTime="false"
            @update:value="(v) => $set(filterItem, 'value', v)"
            style="top: unset"
          />

          <DateTimePicker
            v-else-if="getSelectedTypeString(filterItem) == 'date_value'"
            :value="filterItem.value ? new Date(filterItem.value) : null"
            @update:value="(v) => $set(filterItem, 'value', v)"
            style="top: unset"
          />

          <div class="date-range-filter" v-else-if="getSelectedTypeString(filterItem) == 'date_range'">
            <div class="flex-container date-range-controls">
              <DateTimePicker
                :value="filterItem.from ? new Date(filterItem.from) : null"
                :showTime="getDateRangeShowTime(filterItem)"
                @update:value="(v) => $set(filterItem, 'from', v)"
                style="top: unset"
              />
              <span>to</span>
              <DateTimePicker
                :value="filterItem.to ? new Date(filterItem.to) : null"
                :showTime="getDateRangeShowTime(filterItem)"
                @update:value="(v) => $set(filterItem, 'to', v)"
                style="top: unset"
              />
            </div>
            <div class="date-range-hint">Leave empty for today</div>
          </div>

          <Dropdown
            v-else-if="getSelectedTypeString(filterItem) == 'dropdown_list'"
            :items="getFilterOptions(filterItem)"
            itemKey="label"
            :selectedObject="findFromDropdown(filterItem.value, getFilterOptions(filterItem), filterItem.key)"
            @selected="(e) => groupStringSelected(filterItem.uid, e, filterItem.key)"
            :placeholder="filterItem.label"
          />

          <MultiSelect
            v-else-if="getSelectedTypeString(filterItem) == 'multiselect'"
            :items="getFilterOptions(filterItem)"
            :selectedObject.sync="filterItem.selected"
            itemKey="label"
            :valueKey="getFilterSaveKey(filterItem)"
          />

          <div class="flex-container" v-else-if="getSelectedTypeString(filterItem) == 'boolean'">
            <span
              :class="['join-btn standalone-btn', { active: filterItem.value === true }]"
              @click="booleanSelected(filterItem.uid, true)"
              >Yes</span
            >
            <span
              :class="['join-btn standalone-btn', { active: filterItem.value === false }]"
              @click="booleanSelected(filterItem.uid, false)"
              >No</span
            >
          </div>

          <Dropdown
            v-else-if="getSelectedTypeString(filterItem) == 'null_check'"
            :items="nullCheckOptions"
            :selectedObject="nullCheckOptions.find((o) => o.key === filterItem.value)"
            itemKey="label"
            @selected="(e) => nullCheckSelected(filterItem.uid, e)"
          />

          <div class="filter-error" v-if="filterItem.error">{{ filterItem.error }}</div>
        </div>
      </div>
      <div v-if="filterItems.length < 1" class="no-filters">
        <div>No filter</div>
      </div>
    </div>

    <div class="flex-container mt-auto bottom-cta">
      <Button @click="clearAllFilters" :disabled="filterItems.length < 1">Clear All</Button>
      <Button @click="saveFilters" class="ml-auto">Save</Button>
      <Button @click="cancelFilters">Cancel</Button>
    </div>
  </div>
</template>

<script>
//default demo types
const FILTER_TYPES = [
  { label: 'String value', filterType: 'string_value', key: 'string_value' },
  { label: 'Number value', filterType: 'number_value', key: 'number_value', validator: 'positiveInteger' },
  { label: 'String range', filterType: 'string_range', key: 'string_range', validator: 'positiveDecimal' },
  { label: 'Date', filterType: 'date', key: 'date' },
  { label: 'Date value', filterType: 'date_value', key: 'date_value' },
  { label: 'Date range', filterType: 'date_range', key: 'date_range' },
  {
    label: 'Dropdown list',
    filterType: 'dropdown_list',
    key: 'dropdown_list',
    options: [
      { label: 'one', key: 'oneKey' },
      { label: 'two', key: 'twoKey' },
    ],
  },
  {
    label: 'Mutliselect',
    filterType: 'multiselect',
    key: 'multiselect',
    options: [
      { label: 'one', key: 'oneKey' },
      { label: 'two', key: 'twoKey' },
    ],
  },
  { label: 'Boolean', filterType: 'boolean', key: 'boolean' },
  { label: 'Null check', filterType: 'null_check', key: 'null_check' },
];

const NULL_CHECK_OPTIONS = [
  { label: 'Any', key: 'any' },
  { label: 'Has value', key: 'has_value' },
  { label: 'Is empty', key: 'is_empty' },
];

function makeFilterItem() {
  return {
    uid: crypto.randomUUID(),
    joinType: 'AND',
    filterType: null,
    value: null,
    from: null,
    to: null,
    fromStr: '',
    toStr: '',
    selected: [],
  };
}

export default {
  computed: {
    // use filterDefinition prop if provided, otherwise fall back to generic FILTER_TYPES
    availableTypes() {
      return this.filterDefinition && this.filterDefinition.length > 0 ? this.filterDefinition : FILTER_TYPES;
    },

    canAddFilter() {
      return this.filterItems.length < this.availableTypes.length;
    },
  },

  data: function () {
    return {
      filterItems: [],
      nullCheckOptions: NULL_CHECK_OPTIONS,
      originalItems: [], // snapshot of value on open, used for cancel
      loadedOptions: {},
    };
  },
  props: {
    value: {
      type: Array,
      default: () => [],
    },
    filterDefinition: {
      type: Array,
      default: () => [],
    },
    optionCache: {
      type: Object,
      default: () => ({}),
    },
  },
  methods: {
    // returns available types excluding already-used keys, except for the current item
    getAvailableTypes(uid) {
      const usedKeys = this.filterItems.filter((item) => item.uid !== uid).map((item) => item.key);
      return this.availableTypes.filter((t) => !usedKeys.includes(t.key));
    },

    // returns the selected type object for the dropdown to display
    getSelectedType(filterItem) {
      const find = this.availableTypes.find((t) => t.key === filterItem.key) || null;
      return find;
    },

    getSelectedTypeString(filterItem) {
      const find = this.getSelectedType(filterItem);
      return find?.filterType ?? '';
    },

    getDateRangeShowTime(filterItem) {
      const selectedType = this.getSelectedType(filterItem);
      return !selectedType || selectedType.showTime !== false;
    },

    getFilterSaveKey(filterItem) {
      const selectedType = this.getSelectedType(filterItem);
      return selectedType?.saveKey || 'key';
    },

    // returns the options array for dropdown_list type
    getFilterOptions(filterItem) {
      const def = this.availableTypes.find((t) => t.key === filterItem.key);
      if (!def) return [];
      if (def.optionsFunction) return this.loadedOptions[def.key] ?? [];
      return def.options ?? [];
    },

    hasFilterValue(value) {
      return value !== null && value !== undefined && value !== '';
    },

    optionValueMatches(optionValue, savedValue) {
      if (!this.hasFilterValue(optionValue) || !this.hasFilterValue(savedValue)) return optionValue === savedValue;
      return String(optionValue) === String(savedValue);
    },

    addFilter() {
      if (!this.canAddFilter) return;
      const newItem = makeFilterItem();
      this.filterItems.push(newItem);
    },

    async filterTypeSelected(uid, e) {
      if (!uid || !e) return;
      const item = this.filterItems.find((item) => item.uid == uid);
      if (!item) return;
      // reset values when type changes
      this.$set(item, 'label', e.label);
      this.$set(item, 'key', e.key);
      this.$set(item, 'filterType', e.filterType);
      this.$set(item, 'filterType', e.filterType);
      this.$set(item, 'value', null);
      this.$set(item, 'from', null);
      this.$set(item, 'to', null);
      this.$set(item, 'fromStr', '');
      this.$set(item, 'toStr', '');
      this.$set(item, 'selected', []);
      this.$set(item, 'saveKey', e.saveKey ?? 'key');

      // load options if the selected type has an optionsFunction
      if (e.optionsFunction && !this.loadedOptions[e.key]) {
        await this.loadFilterOptions(e);
      }
    },

    filterItemDelete(uid) {
      this.filterItems = this.filterItems.filter((item) => item.uid != uid);
    },

    filterJoinClick(uid, joinType) {
      const item = this.filterItems.find((item) => item.uid == uid);
      if (item) this.$set(item, 'joinType', joinType);
    },

    booleanSelected(uid, e) {
      const item = this.filterItems.find((item) => item.uid == uid);
      if (item) this.$set(item, 'value', e);
    },

    getValidatorString(key) {
      const selectedType = this.availableTypes.find((t) => t.key == key);
      if (!selectedType) return null;
      return selectedType.validator ?? '';
    },

    findFromDropdown(keyValue, options, filterKey) {
      if (!this.hasFilterValue(keyValue)) return;
      const selectedType = this.availableTypes.find((t) => t.key == filterKey);
      if (!selectedType) return;

      const saveKey = selectedType.saveKey || 'key';
      const find = options.find((item) => this.optionValueMatches(item[saveKey], keyValue));
      return find;
    },

    groupStringSelected(uid, e, filterKey) {
      const item = this.filterItems.find((item) => item.uid == uid);
      if (!item || !e) return;

      const selectedType = this.availableTypes.find((t) => t.key == filterKey);
      const { saveKey } = selectedType;

      this.$set(item, 'value', e[saveKey || 'key']);
    },

    nullCheckSelected(uid, e) {
      const item = this.filterItems.find((item) => item.uid == uid);
      if (item && e) this.$set(item, 'value', e.key);
    },

    cancelFilters() {
      // restore to original snapshot
      this.filterItems = this.originalItems.map((item) => ({ ...item }));
      this.$emit('cancel');
    },

    async clearAllFilters() {
      const discard = await this.$showAlert('warning', 'Are you sure you want to clear all filters?', '', [
        { text: 'Delete', color: 'Red', textColor: 'white' },
        { text: 'Cancel' },
      ]);
      if (discard === 'Cancel') return false;
      this.filterItems = [];
    },

    saveFilters() {
      this.clearErrors();
      const valid = this.validateFilters();
      if (!valid) return;

      this.$emit(
        'update:value',
        this.filterItems.map((item) => {
          // eslint-disable-next-line no-unused-vars
          const { uid, ...rest } = item;
          return rest;
        }),
      );
      this.$emit('save');
    },

    initFromValue(val) {
      const items = (val || []).map((item) => ({
        ...item,
        selected: item.selected ? [...item.selected] : [],
        uid: crypto.randomUUID(),
        error: null,
      }));
      this.filterItems = items.map((item) => {
        const findFull = this.availableTypes.find((t) => t.key == item.key);
        return { ...item, ...findFull };
      });
      this.originalItems = items.map((item) => ({ ...item }));

      items.forEach(async (item) => {
        const selectedType = this.availableTypes.find((t) => t.key == item.key);
        if (selectedType && selectedType.optionsFunction) await this.loadFilterOptions(selectedType);
      });
    },

    async loadFilterOptions(filterDefinition) {
      if (!filterDefinition?.optionsFunction) return;
      if (this.loadedOptions[filterDefinition.key]) return;

      const result = await filterDefinition.optionsFunction();
      this.$set(this.loadedOptions, filterDefinition.key, result);
      this.$emit('update:optionCache', { ...this.loadedOptions });
    },

    validateFilters() {
      let valid = true;

      this.filterItems.forEach((item) => {
        let error = null;
        const type = this.getSelectedTypeString(item);

        switch (type) {
          case 'string_value':
            if (!item.value || !item.value.trim()) error = 'Cannot be empty';
            break;
          case 'number_value':
            if (item.value === null || item.value === undefined || item.value === '') error = 'Cannot be empty';
            else if (!/^\d+$/.test(item.value.toString())) error = 'Must be a positive integer';
            break;
          case 'string_range':
            if (!item.fromStr) error = 'From cannot be empty';
            else if (!item.toStr) error = 'To cannot be empty';
            break;
          case 'date':
            if (!item.value) error = 'Cannot be empty';
            break;
          case 'date_value':
            if (!item.value) error = 'Cannot be empty';
            break;
          case 'date_range':
            if (!item.from) error = 'From cannot be empty';
            break;
          case 'dropdown_list':
            if (!this.hasFilterValue(item.value)) error = 'Cannot be empty';
            break;
          case 'multiselect':
            if (!item.selected || item.selected.length < 1) error = 'Select at least one';
            break;
          case 'boolean':
            if (item.value === null || item.value === undefined) error = 'Must select Yes or No';
            break;
          case '':
          case null:
            error = 'Please select a filter type';
            break;
          default:
            error = 'Error cannot find type';
        }

        this.$set(item, 'error', error);
        if (error) valid = false;
      });

      return valid;
    },

    clearErrors() {
      this.filterItems.forEach((item) => {
        this.$set(item, 'error', null);
      });
    },
  },
  mounted() {
    this.loadedOptions = { ...this.optionCache };
    this.initFromValue(this.value);
  },
  watch: {
    value(val) {
      this.initFromValue(val);
    },
    optionCache: {
      handler(val) {
        this.loadedOptions = { ...(val || {}) };
      },
      deep: true,
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/css/all.scss';

.filter-popup-wrapper {
  width: 100%;
  max-width: 30rem;
  min-height: 20rem;
  max-height: 90dvh;
  overflow: auto;
  background-color: white;
  padding-bottom: 0;
  border-radius: 0.25rem;

  display: flex;
  flex-direction: column;

  .title-wrapper {
    padding: 0.5rem;
    border-bottom: 1px solid gray;
    position: sticky;
    top: 0;
    background: white;
    z-index: 1;
  }

  .filter-list {
    padding: 1rem 0.5rem;
  }

  .filter-item {
    padding: 0.5rem;
    border-radius: 0.25rem;
    border: 1px solid gray;
  }

  .no-filters {
    width: 100%;
    flex: 1;
    display: flex;
    justify-content: center;
    align-items: center;
    border-radius: 0.25rem;
    padding: 1rem;
    color: grey;
  }

  position: relative;

  .bottom-cta {
    position: sticky;
    bottom: 0;
    border-top: 1px solid gray;
    padding: 0.5rem;
    background: white;
  }

  .join-toggle {
    display: flex;
    gap: 1px;
    background: lightgray;
    border: 1px solid lightgray;
    border-radius: 0.25rem;
    overflow: hidden;
  }

  .join-btn {
    background: #fafafa;
    padding: 0.25rem 0.5rem;
    cursor: pointer;
    width: 2.5rem;
    display: flex;
    justify-content: center;
    user-select: none;
    &.active {
      background: #b3c4ff;
    }

    &.standalone-btn {
      border-radius: 0.25rem;
      border: 1px solid lightgray;
      overflow: hidden;
    }
  }

  .filter-error {
    font-size: 0.7rem;
    color: crimson;
    margin-top: 0.25rem;
  }

  .date-range-filter {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
  }

  .date-range-hint {
    color: #6c757d;
    font-size: 0.7rem;
  }
}

.flex-container {
  display: flex;
  gap: 0.5rem;
  align-items: center;
}
</style>
